import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { recordCampaignProgress } from '../api';
import { campaignQuery } from '../queries';
import { navigate, usePath } from '../router';
import { parsePath } from '../../shared/pages';
import type { MemberCampaign } from '../../shared/campaigns';
import { toastFailed } from '../toast';
import { Tour } from './Tour';
import './CampaignTour.css';

const CampaignContext = createContext<{
  teamId: string;
  onSubsOpened(teamId: string): void;
} | null>(null);
export function useCampaignTour() {
  return useContext(CampaignContext);
}

export function CampaignProvider({
  children,
  impersonating,
}: {
  children: ReactNode;
  impersonating: boolean;
}) {
  const { data: campaign } = useQuery(campaignQuery());
  return (
    <ActiveCampaign
      key={String(impersonating)}
      campaign={campaign ?? null}
      impersonating={impersonating}
    >
      {children}
    </ActiveCampaign>
  );
}

function ActiveCampaign({
  campaign,
  impersonating,
  children,
}: {
  campaign: MemberCampaign | null;
  impersonating: boolean;
  children: ReactNode;
}) {
  const root = useRef<HTMLDivElement>(null);
  const client = useQueryClient();
  const [atDestination, setAtDestination] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [visible, setVisible] = useState(false);
  const enrollment = useRef<Promise<unknown> | null>(null);
  const path = parsePath(usePath());
  const home = path.section === 'home';
  const teamPage =
    path.section === 'league' && !path.subsSeason && !path.registrationSeason;
  const step = home ? campaign?.steps[0] : campaign?.steps[1];
  const target = home
    ? '[data-tour="league"]'
    : `[data-tour-team="${campaign?.teamId}"] [role="tab"][id$="-tab-subs"]`;
  const active =
    Boolean(campaign) && !hidden && !atDestination && (home || teamPage);

  const onShown = useCallback(() => {
    setVisible(true);
    if (impersonating) return;
    if (campaign && !campaign.enrolled && !enrollment.current) {
      enrollment.current = recordCampaignProgress(
        campaign.key,
        'enrolled',
        campaign.teamId,
      )
        .then(() =>
          client.setQueryData(campaignQuery().queryKey, (current) =>
            current ? { ...current, enrolled: true } : current,
          ),
        )
        .catch(() => {
          enrollment.current = null;
        });
    }
  }, [campaign, client, impersonating]);

  async function dismiss() {
    if (hidden || !campaign) return;
    setHidden(true);
    if (impersonating) return;
    try {
      await enrollment.current;
      await recordCampaignProgress(campaign.key, 'dismissed', campaign.teamId);
      client.setQueryData(campaignQuery().queryKey, null);
    } catch {
      setHidden(false);
      toastFailed(
        "We couldn't save your choice to hide the tour. Please try again.",
      );
    }
  }

  const campaignTeam = campaign?.teamId;
  const context = useMemo(
    () =>
      campaignTeam
        ? {
            teamId: campaignTeam,
            onSubsOpened(teamId: string) {
              if (teamId === campaignTeam) setAtDestination(true);
            },
          }
        : null,
    [campaignTeam],
  );

  useEffect(() => {
    setVisible(false);
    setAtDestination(false);
  }, [home, path.teamId]);

  return (
    <CampaignContext.Provider value={context}>
      <div
        ref={root}
        className="portal-campaign"
        data-campaign-visible={active && visible}
      >
        {children}
        {active && step && (
          <Tour
            scope={root}
            target={target}
            desktopAnchor={home ? ':scope > svg:last-child' : undefined}
            note={step.message}
            instruction={
              home ? 'Tap League to continue.' : 'Tap Subs to continue.'
            }
            step={home ? 1 : 2}
            completed={false}
            approach={home ? 'side' : 'bottom'}
            onHide={() => void dismiss()}
            onShown={onShown}
            onGo={
              home ? () => navigate(`/league/${campaign?.teamId}`) : undefined
            }
          />
        )}
      </div>
    </CampaignContext.Provider>
  );
}
